import { useMemo, useState } from 'react'
import { LineChart, Line, LabelList, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import SelectInput from "Components/molecule/inputs/select-input"
import CheckboxInput from "Components/molecule/inputs/checkbox-input"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { colorFromIndex } from "utils/color"
import { BillabilityGroupBy, ITrendPoint, ITrendGroupPoint, ITrendTarget } from "models/billability"

interface BillabilityTrendChartProps {
    trend: ITrendPoint[]
    trendByGroup: ITrendGroupPoint[]
    groupBy: BillabilityGroupBy
    targets: ITrendTarget[]
}

// Deliberately outside the groupColor HSL rotation so the benchmark can never be
// mistaken for one of the group series in Details view.
const TARGET_LABEL = 'Target'
const TARGET_COLOR = '#6c757d'

const metricOptions = [
    { value: 'utilization', label: 'Utilization %' },
    { value: 'billable', label: 'Billable %' },
]

const viewOptions = [
    { value: 'average', label: 'Average' },
    { value: 'details', label: 'Details' },
]

// A department with no colour assigned falls back to one derived from its position in the
// series, so every line is distinguishable rather than sharing a single grey.

// The single company-wide line in Average view. Not a department, so it takes no colour
// from the data.
const AVERAGE_COLOR = 'hsl(0, 65%, 45%)'

const BillabilityTrendChart = ({ trend, trendByGroup, groupBy, targets }: BillabilityTrendChartProps) => {
    const [metric, setMetric] = useState<'utilization' | 'billable'>('billable')
    const [view, setView] = useState<'average' | 'details'>('average')
    const [showLabels, setShowLabels] = useState<boolean>(true)

    const detailsAvailable = groupBy !== 'employee'
    const effectiveView = detailsAvailable ? view : 'average'
    const metricKey = metric === 'utilization' ? 'utilizationPct' : 'actualBillablePct'
    const metricLabel = metric === 'utilization' ? 'Utilization %' : 'Billable %'

    const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)
    const periodLabel = (fp: number) => calendar.fiscalToLabel(Math.floor(fp / 100), fp % 100)

    // Targets are per fiscal year, so the value repeats across that year's periods and
    // steps at the year boundary. Undefined where no target is set -- Recharts draws a
    // gap rather than inventing a benchmark.
    const targetFor = (fiscalPeriod: number) => {
        const t = targets.find(x => x.fiscalPeriod === fiscalPeriod)
        const value = metric === 'utilization' ? t?.utilizationTarget : t?.actualBillableTarget
        return value ?? undefined
    }

    const { chartData, seriesNames, colorByName } = useMemo(() => {
        if (effectiveView === 'average') {
            return {
                chartData: trend.map(p => ({
                    name: periodLabel(p.fiscalPeriod),
                    [metricLabel]: p[metricKey],
                    [TARGET_LABEL]: targetFor(p.fiscalPeriod),
                })),
                seriesNames: [metricLabel],
                colorByName: new Map<string, string | null>([[metricLabel, AVERAGE_COLOR]]),
            }
        }

        const names = Array.from(new Set(trendByGroup.map(p => p.groupName))).sort()
        const colorByName = new Map(trendByGroup.map(p => [p.groupName, p.groupColor]))
        const byPeriod = new Map<number, any>()
        for (const point of trendByGroup) {
            if (!byPeriod.has(point.fiscalPeriod)) {
                byPeriod.set(point.fiscalPeriod, {
                    name: periodLabel(point.fiscalPeriod),
                    [TARGET_LABEL]: targetFor(point.fiscalPeriod),
                })
            }
            byPeriod.get(point.fiscalPeriod)[point.groupName] = point[metricKey]
        }
        const data = Array.from(byPeriod.keys()).sort((a, b) => a - b).map(fp => byPeriod.get(fp))
        return { chartData: data, seriesNames: names, colorByName }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveView, metric, trend, trendByGroup, targets])

    if (chartData.length === 0) return null

    const hasTarget = chartData.some(d => d[TARGET_LABEL] !== undefined)

    return (
        <div style={{ width: "100%" }}>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '10px' }}>
                <CheckboxInput
                    label="Show Labels"
                    checked={showLabels}
                    onChange={() => setShowLabels(prev => !prev)}
                />
                {detailsAvailable && (
                    <SelectInput
                        options={viewOptions}
                        inputClassName="m-0"
                        value={view}
                        onChange={(e) => setView(e.target.value as 'average' | 'details')}
                    />
                )}
                <SelectInput
                    options={metricOptions}
                    inputClassName="m-0"
                    value={metric}
                    onChange={(e) => setMetric(e.target.value as 'utilization' | 'billable')}
                />
            </div>
            <div style={{ width: "100%", height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(v: number) => `${v.toFixed(0)}%`} />
                        <Tooltip formatter={(v: number) => `${v.toFixed(0)}%`} wrapperStyle={{ zIndex: 1000 }} />
                        <Legend />
                        {seriesNames.map((name, i) => (
                            <Line
                                key={name}
                                type="monotone"
                                dataKey={name}
                                stroke={colorByName.get(name) ?? colorFromIndex(i, seriesNames.length)}
                                dot
                            >
                                {showLabels && (
                                    <LabelList
                                        dataKey={name}
                                        position="top"
                                        formatter={(v: number) => `${v.toFixed(0)}%`}
                                    />
                                )}
                            </Line>
                        ))}
                        {hasTarget && (
                            <Line
                                key={TARGET_LABEL}
                                type="stepAfter"
                                dataKey={TARGET_LABEL}
                                stroke={TARGET_COLOR}
                                strokeDasharray="4 4"
                                dot={false}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}

export default BillabilityTrendChart
